import React, { useState } from 'react';
import { Agent } from '../../types';
import { testApiKey } from '../../services/translation';
import {
  signIn, completeNewPassword, isConfigured, friendlyAuthError,
  AuthPrincipal,
} from '../../services/cognitoAuth';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import s from './LoginScreen.module.css';

interface Props { onLogin: (agent: Agent, apiKey: string) => void; }

/** Build the app's Agent shape from the Cognito principal (name/initials/role). */
function agentFromPrincipal(p: AuthPrincipal): Agent {
  const display = (p.name || p.email || p.username || 'User').trim();
  const initials =
    display
      .split(/[\s@._-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0]!.toUpperCase())
      .join('') || 'US';
  return {
    id: p.sub || p.username,
    name: display,
    initials,
    role: p.groups.includes('saas-admin') ? 'SaaS Admin' : 'Support Agent',
    color: '#009EDB',
    password: '',
  };
}

/**
 * AppLogin
 * ─────────────────────────────────────────────────────────────────────────────
 * The single sign-in for the whole back-office app (Support Console + SaaS
 * Admin), replacing the old hardcoded agent-grid login. Authenticates against
 * the Cognito User Pool; the ID token also authorises the SaaS-Admin
 * feature-config API. Handles the first-login NEW_PASSWORD_REQUIRED challenge.
 * The optional Claude API-key field (LLM translation for the Support Console) is
 * preserved.
 */
export const AppLogin: React.FC<Props> = ({ onLogin }) => {
  const configured = isConfigured();

  const [phase, setPhase] = useState<'signin' | 'newPassword'>('signin');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [newPw, setNewPw]       = useState('');
  const [newPw2, setNewPw2]     = useState('');
  const [pendingUser, setPendingUser] = useState<CognitoUser | null>(null);

  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState('');

  const [apiKey,  setApiKey]  = useState(() => localStorage.getItem('lp_ak') ?? '');
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState<'ok' | 'fail' | null>(null);

  const finish = (principal: AuthPrincipal) => {
    if (apiKey) localStorage.setItem('lp_ak', apiKey);
    onLogin(agentFromPrincipal(principal), apiKey);
  };

  const submitSignIn = async () => {
    if (!configured || busy || !username.trim() || !password) return;
    setBusy(true); setError('');
    try {
      const res = await signIn(username.trim(), password);
      if (res.status === 'ok') { finish(res.principal); return; }
      setPendingUser(res.user);
      setPhase('newPassword');
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async () => {
    if (busy || !pendingUser) return;
    if (newPw.length < 8)  { setError('Password must be at least 8 characters.'); return; }
    if (newPw !== newPw2)  { setError('The two passwords do not match.'); return; }
    setBusy(true); setError('');
    try {
      const res = await completeNewPassword(pendingUser, newPw);
      if (res.status === 'ok') finish(res.principal);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  };

  const tryTest = async () => {
    if (!apiKey.trim()) return;
    setTesting(true); setTestRes(null);
    setTestRes((await testApiKey(apiKey)) ? 'ok' : 'fail');
    setTesting(false);
  };

  const canSignIn = configured && !busy && username.trim().length > 0 && password.length > 0;

  return (
    <div className={s.screen}>
      <div className={s.grid} />
      <div className={s.glow} />
      <div className={s.card}>
        <div className={s.brand}>
          <div className=""><img src="../assets/logo.png" alt="Logipulse" /></div>
          <div><div className={s.titleText}>Logipulse</div><div className={s.brandSub}>Back Office</div></div>
        </div>

        {!configured && (
          <div style={{
            background: 'rgba(244,63,94,0.08)', border: '1px solid var(--red-ring, rgba(244,63,94,0.35))',
            borderRadius: 'var(--r)', padding: '10px 12px', marginBottom: 18,
            fontSize: 11.5, lineHeight: 1.55, color: 'var(--tx-secondary)',
          }}>
            <strong style={{ color: 'var(--tx-primary)' }}>Cognito is not configured.</strong> Set{' '}
            <code>REACT_APP_COGNITO_USER_POOL_ID</code> and <code>REACT_APP_COGNITO_CLIENT_ID</code>{' '}
            in the app's <code>.env</code>, then restart the dev server.
          </div>
        )}

        {phase === 'signin' && (
          <>
            <h1 className={s.heading}>Sign in</h1>
            <p className={s.sub}>Sign in to the Support Console & SaaS Admin</p>

            <section className={s.section}>
              <div className={s.label}>Email</div>
              <div className={s.keyRow}>
                <input
                  className={s.input}
                  type="text"
                  placeholder="you@logipulse.com"
                  autoComplete="username"
                  value={username}
                  autoFocus
                  disabled={!configured || busy}
                  onChange={e => { setUsername(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && canSignIn && submitSignIn()}
                />
              </div>
            </section>

            <section className={s.section}>
              <div className={s.label}>Password</div>
              <div className={s.keyRow}>
                <input
                  className={`${s.input} ${error ? s.inputError : ''}`}
                  type={showPw ? 'text' : 'password'}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={password}
                  disabled={!configured || busy}
                  onChange={e => { setPassword(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && canSignIn && submitSignIn()}
                />
                <button className={s.testBtn} type="button" onClick={() => setShowPw(v => !v)} title={showPw ? 'Hide' : 'Show'}>
                  {showPw ? 'X' : '👁'}
                </button>
              </div>
              {error && <div className={s.testFail}>✗ {error}</div>}
            </section>

            {/* ── Optional Claude API Key (LLM translation for the Support Console) ── */}
            <section className={s.section}>
              <div className={s.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Claude API Key</span>
                <span className={s.optionalBadge}>Optional</span>
              </div>
              <div className={s.keyRow}>
                <input className={s.input} type="password" placeholder="sk-ant-api03-… (leave blank to skip)"
                  value={apiKey} onChange={e => { setApiKey(e.target.value); setTestRes(null); }} />
                <button className={s.testBtn} disabled={!apiKey.trim() || testing} onClick={tryTest}>
                  {testing ? '…' : 'Test'}
                </button>
              </div>
              {testRes === 'ok'   && <div className={s.testOk}>✓ Key is valid — LLM translation ready</div>}
              {testRes === 'fail' && <div className={s.testFail}>✗ Key test failed — check and try again</div>}
            </section>

            <button className={s.submit} disabled={!canSignIn} onClick={submitSignIn}>
              {busy ? 'Signing in…' : !configured ? 'Cognito not configured' : 'Sign in →'}
            </button>
          </>
        )}

        {phase === 'newPassword' && (
          <>
            <h1 className={s.heading}>Set a new password</h1>
            <p className={s.sub}>Your account requires a new password before first use</p>

            <section className={s.section}>
              <div className={s.label}>New password</div>
              <div className={s.keyRow}>
                <input
                  className={`${s.input} ${error ? s.inputError : ''}`}
                  type={showPw ? 'text' : 'password'}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  value={newPw}
                  autoFocus
                  disabled={busy}
                  onChange={e => { setNewPw(e.target.value); setError(''); }}
                />
                <button className={s.testBtn} type="button" onClick={() => setShowPw(v => !v)} title={showPw ? 'Hide' : 'Show'}>
                  {showPw ? 'X' : '👁'}
                </button>
              </div>
            </section>

            <section className={s.section}>
              <div className={s.label}>Confirm new password</div>
              <div className={s.keyRow}>
                <input
                  className={`${s.input} ${error ? s.inputError : ''}`}
                  type={showPw ? 'text' : 'password'}
                  placeholder="Re-enter the new password"
                  autoComplete="new-password"
                  value={newPw2}
                  disabled={busy}
                  onChange={e => { setNewPw2(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && submitNewPassword()}
                />
              </div>
              {error && <div className={s.testFail}>✗ {error}</div>}
            </section>

            <button className={s.submit} disabled={busy} onClick={submitNewPassword}>
              {busy ? 'Saving…' : 'Set password & continue →'}
            </button>
            <p className={s.skipNote}>
              <button
                type="button"
                onClick={() => { setPhase('signin'); setError(''); setPendingUser(null); }}
                style={{ background: 'none', border: 'none', color: 'var(--tx-muted)', cursor: 'pointer', textDecoration: 'underline' }}
              >
                ← Back to sign in
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
};
